import { createContext, memo, type PropsWithChildren, useContext, useMemo } from 'react';

import { type ScopedPermissions } from '@air/api';

type DiscussionPermissionsProviderContextValue = {
  permissions: ScopedPermissions | undefined;
};

const DISCUSSION_PERMISSIONS_CONTEXT_DEFAULT_VALUE: DiscussionPermissionsProviderContextValue = {
  permissions: undefined,
};

const DiscussionPermissionsProviderContext = createContext<DiscussionPermissionsProviderContextValue>(
  DISCUSSION_PERMISSIONS_CONTEXT_DEFAULT_VALUE,
);

export type DiscussionPermissionsProviderProps = PropsWithChildren<{
  permissions: ScopedPermissions | undefined;
}>;

export const DiscussionPermissionsProvider = memo(({ children, permissions }: DiscussionPermissionsProviderProps) => {
  const value = useMemo(() => ({ permissions }), [permissions]);

  return (
    <DiscussionPermissionsProviderContext.Provider value={value}>
      {children}
    </DiscussionPermissionsProviderContext.Provider>
  );
});

DiscussionPermissionsProvider.displayName = 'DiscussionPermissionsProvider';

export function useDiscussionPermissions() {
  const context = useContext(DiscussionPermissionsProviderContext);

  if (context === DISCUSSION_PERMISSIONS_CONTEXT_DEFAULT_VALUE) {
    throw new Error('useDiscussionPermissions must be used within a DiscussionPermissionsProvider');
  }

  return context;
}
